import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@vitesse/base-ui/elements/vitesse-dialog';
import '@vitesse/base-ui/elements/inputs/vitesse-select';
import '@vitesse/base-ui/elements/inputs/vitesse-text-input';
import { button, baseButtonStyles } from '@vitesse/base-ui/components/base-buttons';
import { t, LocaleController } from '@vitesse/base-ui/i18n';
import type { IntegrationOp } from '../../../core/domain/integration';
import { parseSpec, methodColor } from './endpoint-method';

/*
 * <vitesse-endpoint-op-editor> — the "Operations" dialog for a REST integration:
 * the hand-added operations it has, and a small form to add one. This is the
 * fallback for every internal API without an OpenAPI spec — the common case —
 * so it is shaped like adding a request in Postman: name, method, path.
 *
 * DUMB by design (components never fetch): it renders `.ops` and emits
 * `ops-change` with the full new list; the viewport owns the apply. Spec-
 * discovered operations are NOT listed here — they are re-derived on every
 * connect, so removing one would be meaningless; only hand-added ones persist.
 *
 * Read-vs-write follows the driver's own rule (GET/HEAD/OPTIONS read, anything
 * else writes) so what you add here is exactly what discovery would have made.
 */

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];
const READ_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

@customElement('vitesse-endpoint-op-editor')
export class EndpointOpEditor extends LitElement {
  @property({ type: Boolean }) open = false;
  /** The integration's hand-added operations (descriptor.targets). */
  @property({ attribute: false }) ops: IntegrationOp[] = [];
  /** True while the viewport's apply is in flight — locks the form. */
  @property({ type: Boolean }) busy = false;
  /** The server's answer when an apply was refused — already prose. */
  @property() error = '';

  @state() private name = '';
  @state() private method = 'GET';
  @state() private path = '';
  @state() private invalid = false;

  constructor() {
    super();
    new LocaleController(this);
  }

  // Shadow root + `button()` ⇒ baseButtonStyles is required, exactly as in the
  // viewport that opens this dialog: the global base-button sheet stops at the
  // boundary, so the per-row remove and the Add button would otherwise be bare
  // browser buttons. Its sibling topic-subscription-editor.ts does the same.
  static styles = [
    baseButtonStyles,
    css`
    .list {
      display: flex;
      flex-direction: column;
      gap: 4px;
      margin-bottom: 14px;
      max-height: 40vh;
      overflow-y: auto;
    }
    .op {
      display: flex;
      align-items: center;
      gap: 9px;
      padding: 6px 8px;
      border: 1px solid var(--border-color);
      border-radius: 6px;
      font-size: 13px;
    }
    .method {
      flex: none;
      font: 700 10px/1 'SF Mono', ui-monospace, Menlo, monospace;
      letter-spacing: 0.03em;
      padding: 2px 6px;
      border-radius: 4px;
    }
    .op .name {
      color: var(--text-primary);
    }
    .op .url {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-size: 11.5px;
      color: var(--text-tertiary, var(--text-secondary));
      font-family: 'SF Mono', ui-monospace, Menlo, monospace;
    }
    .none {
      font-size: 12.5px;
      color: var(--text-secondary);
      margin-bottom: 14px;
    }
    .form {
      display: grid;
      grid-template-columns: 1fr 110px;
      gap: 8px;
      align-items: end;
    }
    .form .path {
      grid-column: 1 / -1;
    }
    label {
      display: block;
      font-size: 11px;
      color: var(--text-secondary);
      margin-bottom: 4px;
    }
    .hint {
      grid-column: 1 / -1;
      font-size: 11.5px;
      color: var(--text-tertiary, var(--text-secondary));
    }
    .error {
      grid-column: 1 / -1;
      font-size: 12px;
      color: var(--red, #d64541);
    }
  `,
  ];

  private emit(next: IntegrationOp[]): void {
    this.dispatchEvent(new CustomEvent<IntegrationOp[]>('ops-change', { detail: next, bubbles: true, composed: true }));
  }

  private add(): void {
    const name = this.name.trim();
    let path = this.path.trim();
    if (!name || !path) {
      this.invalid = true;
      return;
    }
    if (!path.startsWith('/')) path = `/${path}`;
    this.invalid = false;
    const op: IntegrationOp = { name, mode: READ_METHODS.has(this.method) ? 'read' : 'write', spec: `${this.method} ${path}` };
    // Same name replaces — the driver keys targets by name.
    this.emit([...this.ops.filter((o) => o.name !== name), op]);
    this.name = '';
    this.path = '';
  }

  private removeOp(name: string): void {
    this.emit(this.ops.filter((o) => o.name !== name));
  }

  protected render() {
    return html`<vitesse-dialog
      .open=${this.open}
      heading=${t('integration.opsManage')}
      width="560px"
      @dialog-close=${() => this.dispatchEvent(new CustomEvent('dialog-close', { bubbles: true, composed: true }))}
    >
      ${
        this.ops.length === 0
          ? html`<div class="none">${t('integration.opsNone')}</div>`
          : html`<div class="list">
              ${this.ops.map((o) => {
                const { method, url } = parseSpec(o.spec);
                const [fg, bg] = methodColor(method);
                return html`<div class="op">
                  <span class="method" style="color:${fg};background:${bg}">${method}</span>
                  <span class="name">${o.name}</span>
                  <span class="url" title=${url}>${url}</span>
                  ${button({ icon: 'x', color: 'red', size: 'sm', title: t('integration.opsRemove'), disabled: this.busy, onClick: () => this.removeOp(o.name) })}
                </div>`;
              })}
            </div>`
      }
      <div class="form">
        <div>
          <label>${t('integration.opsName')}</label>
          <vitesse-text-input
            size="sm"
            .value=${this.name}
            ?disabled=${this.busy}
            @change=${(e: CustomEvent<{ value: string }>) => (this.name = e.detail.value)}
          ></vitesse-text-input>
        </div>
        <div>
          <label>${t('integration.opsMethod')}</label>
          <vitesse-select
            size="sm"
            .options=${METHODS.map((m) => ({ value: m, label: m }))}
            .value=${this.method}
            ?disabled=${this.busy}
            @change=${(e: CustomEvent<{ value: string }>) => (this.method = e.detail.value)}
          ></vitesse-select>
        </div>
        <div class="path">
          <label>${t('integration.opsPath')}</label>
          <vitesse-text-input
            size="sm"
            placeholder="/users/{id}"
            .value=${this.path}
            ?disabled=${this.busy}
            @change=${(e: CustomEvent<{ value: string }>) => (this.path = e.detail.value)}
            @keydown=${(e: KeyboardEvent) => e.key === 'Enter' && this.add()}
          ></vitesse-text-input>
        </div>
        <div class="hint">${t('integration.opsPathHint')}</div>
        ${this.invalid ? html`<div class="error">${t('integration.opsInvalid')}</div>` : nothing}
        ${this.error ? html`<div class="error">${this.error}</div>` : nothing}
      </div>
      <span slot="footer"
        >${button({
          label: this.busy ? t('integration.opsSaving') : t('integration.opsAdd'),
          icon: 'plus',
          color: 'blue',
          size: 'sm',
          disabled: this.busy,
          onClick: () => this.add(),
        })}</span
      >
    </vitesse-dialog>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-endpoint-op-editor': EndpointOpEditor;
  }
}
